import dynStorage from '@/utils/storage-dynamic'
import axiosIntents from '@/utils/axios-intents'
import StoreCache from '@/utils/StoreCache.js'
import mapTravelPlan, { mapRecommendedPlan } from '@/helpers/travel-esim-plan'

const STORAGE_KEY = 'travel-esim'

// Travel catalog cache — separate 30-min TTL gate from the mobile catalog
// (catalog_services.js) so the two never collide. Mirrors that module's pattern.
const travelCache = new StoreCache(30 * 60 * 1000)

// In-flight request dedup so concurrent page mounts share one network call.
let travel_plans_request = null

// Product type the BE expects for travel eSIM data plans (per API docs).
const TRAVEL_PRODUCT_TYPE = 'travelMobileAddon'

// Icon per usage tier (Group) returned by POST /catalog/v2/recommend. The
// label/description come from the API response (Profiles[]); only the icon is
// a frontend asset choice.
const USAGE_TIER_ICONS = {
    light: 'chat-box',
    medium: 'thumbs-up',
    heavy: 'play-video',
}

// Apply discount overrides from the Prices[] array onto Entries in place
// (same shape/logic as catalog_services.js).
const applyPriceOverrides = (Entries, Prices) => {
    if (!Prices || !Prices.length) return

    Prices.forEach(price_item => {
        const has_discount = price_item.DiscountedPrice < price_item.OrigianlPrice
        if (!has_discount) return

        const entry = Entries.find(e => e.Availabilities?.[0]?.UUID == price_item.ProductID)
        if (entry && entry.Availabilities?.[0]) {
            entry.origianl_price = price_item.OrigianlPrice
            entry.discount = price_item.OrigianlPrice - price_item.DiscountedPrice
            entry.DiscountUUID = price_item.AppliedOfferID
            entry.Availabilities[0].RetailCents = price_item.DiscountedPrice
        }
    })
}

// Single place that persists the whole flow so every page reads the same
// centralized state. Called after any mutation of the flow.
const persist = (state) => {
    dynStorage.set(STORAGE_KEY, {
        destinations: state.travel_esim_destinations,
        selected_plans: state.travel_esim_selected_plans,
        sims: state.travel_esim_sims,
        active_sim_index: state.travel_esim_active_sim_index,
        trip_seq: state.travel_esim_trip_seq,
        step: state.travel_esim_current_step,
    })
}

// Derive the list of chosen trip plans (one per selected destination) that has
// a plan picked on the "Choose your plan" step. This is the canonical list a
// SIM receives when the customer commits their plan choices.
const buildTripPlans = (state) => {
    return state.travel_esim_destinations
        .filter(d => d.trip_id && state.travel_esim_selected_plans[d.trip_id])
        .map(d => ({
            ...state.travel_esim_selected_plans[d.trip_id],
            trip_id: d.trip_id,
            destination_code: d.code,
            destination_name: d.name,
            // Canonical region key used for de-duping a SIM's plans. Must be the
            // same value both flows produce (planner uses d.name, all-plans uses
            // the active continent) so editing REPLACES instead of appending.
            region: d.name,
        }))
}

// Trip length in days from arrival/departure (inclusive of both days). Falls
// back to 1 when dates are missing/invalid — the recommend API requires >= 1.
const tripDays = (destination) => {
    const { arrival_date, departure_date } = destination || {}
    if (!arrival_date || !departure_date) return 1

    const start = new Date(arrival_date)
    const end = new Date(departure_date)
    if (isNaN(start) || isNaN(end)) return 1

    const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    return days > 0 ? days : 1
}

// Canonical display order for the usage tiers (Light → Medium → Heavy),
// derived from USAGE_TIER_ICONS insertion order, so columns render in a stable
// order regardless of the order the API returns Profiles/Plans in.
const USAGE_TIER_ORDER = Object.keys(USAGE_TIER_ICONS)
const tierOrder = (group) => {
    const i = USAGE_TIER_ORDER.indexOf((group || '').toLowerCase())
    return i === -1 ? USAGE_TIER_ORDER.length : i
}

// Map a /catalog/v2/recommend response for ONE location into the Light/Medium/
// Heavy recommended columns. EVERY tier from the response is rendered: a tier
// WITH a plan becomes a selectable card; a tier with NO Entry becomes a
// placeholder (is_unavailable: true) so the customer still sees the usage level
// exists but has no plan for this destination. Returns [] only when NO tier has
// a plan, so the caller can show the single "no plans for this region" state.
const mapRecommendResponse = (data, region) => {
    if (!data) return []

    const profiles = data.Profiles || []
    const zone = (data.PerZone || [])[0]
    const plans = zone && Array.isArray(zone.Plans) ? zone.Plans : []

    // Usage-tier key: the API uses `ID` on Profiles and `ProfileID` on Plans
    // (older payloads used `Group` on both). Support both so the mapping is
    // resilient to either shape.
    const profileKey = (p) => p && (p.ID || p.Group)
    const planKey = (p) => p && (p.ProfileID || p.Group)

    // Pair each tier with its plan by usage-tier key.
    const plansByTier = {}
    plans.forEach(p => { const k = planKey(p); if (k) plansByTier[k] = p })

    // Tiers come from Profiles (canonical — carries Label/Description, and lists
    // levels even when they have no plan); fall back to plan tiers if absent.
    const tiers = profiles.length ? profiles : plans.map(p => ({ ID: planKey(p) }))

    const rows = tiers
        .filter(profile => profileKey(profile))
        .sort((a, b) => tierOrder(profileKey(a)) - tierOrder(profileKey(b)))
        .map(profile => {
            const tier = profileKey(profile)
            // Normalize the tier to lowercase for usage_group so a plan selected
            // and persisted to localStorage still matches on rehydration even if
            // the BE changes casing between sessions (isSelected compares this).
            const tier_key = (tier || '').toLowerCase()
            const usage = {
                usage_group: tier_key,
                usage_label: profile.Label || '',
                usage_description: profile.Description || '',
                usage_icon: USAGE_TIER_ICONS[tier_key] || 'chat-box',
            }

            const profile_plan = plansByTier[tier]
            const plan = profile_plan ? mapRecommendedPlan(profile_plan, region) : null

            // No plan for this tier → placeholder column (no Select button).
            if (!plan) return { ...usage, is_unavailable: true }

            return { ...plan, ...usage, estimated_mb: profile_plan.EstimatedMB }
        })

    // Region has no plan for ANY tier → return empty so the caller shows the
    // single region-level empty message instead of three placeholders.
    return rows.some(row => !row.is_unavailable) ? rows : []
}

// Hardcoded regions per the design. The BE is expected to return `Regions`
// values that exactly match these strings so plans map to the right tab/trip.
const DESTINATIONS = [
    { code: 'Australia', name: 'Australia' },
    { code: 'New Zealand', name: 'New Zealand' },
    { code: 'Africa', name: 'Africa' },
    { code: 'Pacific', name: 'Pacific' },
    { code: 'Asia', name: 'Asia' },
    { code: 'Europe', name: 'Europe' },
    { code: 'North America', name: 'North America' },
    { code: 'South America', name: 'South America' },
]

// Tabs on the "All travel plans" page — the same set as the destination
// dropdown, derived from DESTINATIONS so the two never drift out of sync.
const CONTINENTS = DESTINATIONS.map(({ name }) => name)

export default {
    state: {
        travel_esim_current_step: 0,
        travel_esim_destinations: [],
        travel_esim_selected_plans: {},
        travel_esim_sims: [],
        travel_esim_sim_count: 1,
        // Which SIM is currently having its plans chosen (used when the
        // customer taps "Choose plan" and jumps back to the plan step).
        travel_esim_active_sim_index: 0,
        // Exact plan slot being edited on the active SIM (set when the pencil is
        // clicked). -1 means "adding a new plan", not editing an existing one.
        travel_esim_active_plan_index: -1,
        // Monotonic counter used to give every trip/destination row a stable
        // unique id, so selected plans are keyed per-trip (two trips to the same
        // country are independent). Avoids Date.now()/random for determinism.
        travel_esim_trip_seq: 0,
        // Travel catalog (fetched from /catalog/v2/entries, ProductTypeList=travelMobileAddon)
        travel_esim_catalog: [],
        travel_esim_catalog_loaded: false,
        // Recommended plans keyed by trip_id (from POST /catalog/v2/recommend, one
        // call per trip). Each value is the mapped Light/Medium/Heavy trio.
        travel_esim_recommendations: {},
        // trip_ids with an in-flight recommend request (used to show a loader and
        // to avoid firing duplicate calls for the same trip).
        travel_esim_recommend_loading: {},
    },

    getters: {
        travel_esim_current_step: (state) => state.travel_esim_current_step,
        travel_esim_destinations: (state) => state.travel_esim_destinations,
        travel_esim_selected_plans: (state) => state.travel_esim_selected_plans,
        travel_esim_sims: (state) => state.travel_esim_sims,
        travel_esim_sim_count: (state) => state.travel_esim_sim_count,
        travel_esim_catalog_loaded: (state) => state.travel_esim_catalog_loaded,

        // All mapped travel plans from the catalog
        travel_esim_catalog: (state) => state.travel_esim_catalog,
        travel_esim_available_plans: (state) => state.travel_esim_catalog,

        // Hardcoded destination dropdown options (BE Regions must match these).
        travel_esim_available_destinations: () => DESTINATIONS,

        // Hardcoded tabs on the "All travel plans" page (BE Regions must match).
        travel_esim_available_continents: () => CONTINENTS,

        // Plans available for a given region/continent name (used by the "All
        // travel plans" browse page, which reads the full catalog, NOT recommend).
        travel_esim_plans_by_region: (state) => (region) =>
            state.travel_esim_catalog.filter(p => (p.regions || []).includes(region)),

        // The recommended Light/Medium/Heavy trio for a given trip, from the
        // per-trip POST /catalog/v2/recommend response (empty until it resolves).
        travel_esim_recommended_plans_for_trip: (state) => (trip_id) =>
            state.travel_esim_recommendations[trip_id] || [],

        // Whether the recommend call for a given trip is still in flight.
        travel_esim_recommend_loading_for_trip: (state) => (trip_id) =>
            !!state.travel_esim_recommend_loading[trip_id],

        // Only destinations the customer has actually filled in (code selected)
        travel_esim_selected_destinations: (state) => state.travel_esim_destinations.filter(d => d.code),
        travel_esim_active_sim_index: (state) => state.travel_esim_active_sim_index,
        travel_esim_active_plan_index: (state) => state.travel_esim_active_plan_index,
    },

    actions: {
        // Fetch travel eSIM plans from the shared catalog endpoint. Mirrors
        // GetCatalogServices (axiosIntents, noauth when logged out, 30-min
        // StoreCache gate, Prices discount overrides) but for travelMobileAddon.
        GetTravelPlans({ getters, commit, state }, { force = false } = {}) {
            if (!force && !travelCache.expired() && state.travel_esim_catalog.length) {
                commit('SET_TRAVEL_ESIM_CATALOG_LOADED', true)
                return Promise.resolve(state.travel_esim_catalog)
            }

            // Dedup concurrent calls
            if (travel_plans_request) return travel_plans_request

            const payload = {
                ProductTypeList: [TRAVEL_PRODUCT_TYPE],
                SearchOptions: { PageSize: 1000 },
            }

            const ID = getters.current_account_uid
            const ProductType = getters.accountType
            const ProductInstanceUUID = getters.customer_current_product_uuid

            if (ID) {
                payload.CustomerInfo = { ID }
                if (ProductType && ProductType != 'Not Activated') {
                    payload.ProductInfo = { ProductType: TRAVEL_PRODUCT_TYPE }
                    if (ProductInstanceUUID) payload.ProductInfo.ProductInstanceUUID = ProductInstanceUUID
                }
            }

            const request_params = ID ? { params: {} } : { params: { noauth: true } }

            commit('SET_TRAVEL_ESIM_CATALOG_LOADED', false)

            travel_plans_request = axiosIntents.post('/catalog/v2/entries', payload, request_params)
                .then(({ data }) => {
                    const { Entries = [], Prices } = data || {}

                    applyPriceOverrides(Entries, Prices)

                    const mapped = Entries.map(mapTravelPlan)
                    commit('SAVE_TRAVEL_ESIM_CATALOG', mapped)

                    return mapped
                })
                .catch(error => {
                    console.log('GetTravelPlans Error', error)
                    commit('RESET_TRAVEL_ESIM_CATALOG')
                    return []
                })
                .finally(() => {
                    commit('SET_TRAVEL_ESIM_CATALOG_LOADED', true)
                    travel_plans_request = null
                })

            return travel_plans_request
        },

        // Fetch the recommended Light/Medium/Heavy trio for ONE trip via
        // POST /catalog/v2/recommend. Called once per trip on the "Choose your
        // plan" step. TravelDays is derived from the trip's arrival/departure
        // dates; Locations is the single destination region for that trip.
        GetTravelRecommendations({ getters, commit, state }, { trip_id, force = false } = {}) {
            if (!trip_id) return Promise.resolve([])

            // Skip if already loaded (unless forced) or a call is in flight.
            if (!force && state.travel_esim_recommendations[trip_id]) {
                return Promise.resolve(state.travel_esim_recommendations[trip_id])
            }
            if (state.travel_esim_recommend_loading[trip_id]) return Promise.resolve([])

            const destination = state.travel_esim_destinations.find(d => d.trip_id === trip_id)
            if (!destination || !destination.code) return Promise.resolve([])

            const payload = {
                Locations: [destination.code],
                TravelDays: tripDays(destination),
            }

            const ID = getters.current_account_uid
            if (ID) payload.CustomerInfo = { CustomerID: ID, TenantID: getters.app_spid }

            const request_params = ID ? { params: {} } : { params: { noauth: true } }

            commit('SET_TRAVEL_ESIM_RECOMMEND_LOADING', { trip_id, loading: true })

            return axiosIntents.post('/catalog/v2/recommend', payload, request_params)
                .then(({ data }) => {
                    const plans = mapRecommendResponse(data, destination.code)
                    commit('SET_TRAVEL_ESIM_RECOMMENDATIONS', { trip_id, plans })
                    return plans
                })
                .catch(error => {
                    console.log('GetTravelRecommendations Error', error)
                    commit('SET_TRAVEL_ESIM_RECOMMENDATIONS', { trip_id, plans: [] })
                    return []
                })
                .finally(() => {
                    commit('SET_TRAVEL_ESIM_RECOMMEND_LOADING', { trip_id, loading: false })
                })
        },

        travelEsimSetStep({ commit }, step) {
            commit('SET_TRAVEL_ESIM_STEP', step)
        },

        travelEsimNextStep({ commit, state }) {
            commit('SET_TRAVEL_ESIM_STEP', state.travel_esim_current_step + 1)
        },

        travelEsimPrevStep({ commit, state }) {
            if (state.travel_esim_current_step > 0) {
                commit('SET_TRAVEL_ESIM_STEP', state.travel_esim_current_step - 1)
            }
        },

        travelEsimAddDestination({ commit, state }) {
            const trip_id = state.travel_esim_trip_seq + 1
            commit('SET_TRAVEL_ESIM_TRIP_SEQ', trip_id)

            const destinations = [...state.travel_esim_destinations, { trip_id, code: '', name: '', arrival_date: '', departure_date: '' }]
            commit('SET_TRAVEL_ESIM_DESTINATIONS', destinations)
            persist(state)
        },

        travelEsimRemoveDestination({ commit, state }, index) {
            const removed = state.travel_esim_destinations[index]
            const destinations = state.travel_esim_destinations.filter((_, i) => i !== index)
            commit('SET_TRAVEL_ESIM_DESTINATIONS', destinations)

            // Drop the selected plan and cached recommendation for the trip that
            // no longer exists
            if (removed && removed.trip_id) {
                const selected_plans = { ...state.travel_esim_selected_plans }
                delete selected_plans[removed.trip_id]
                commit('SET_TRAVEL_ESIM_SELECTED_PLANS', selected_plans)
                commit('CLEAR_TRAVEL_ESIM_RECOMMENDATION', removed.trip_id)
            }
            persist(state)
        },

        travelEsimUpdateDestination({ commit, state }, { index, destination }) {
            const destinations = [...state.travel_esim_destinations]
            const prev = destinations[index] || {}
            destinations[index] = { ...prev, ...destination }
            commit('SET_TRAVEL_ESIM_DESTINATIONS', destinations)

            // If the destination country changed, the old selected plan is no
            // longer valid — drop it (keyed by this trip) so stale plans don't leak.
            if (destination.code !== undefined && destination.code !== prev.code && prev.trip_id) {
                const selected_plans = { ...state.travel_esim_selected_plans }
                delete selected_plans[prev.trip_id]
                commit('SET_TRAVEL_ESIM_SELECTED_PLANS', selected_plans)
            }

            // The recommendation is keyed on destination code + trip length. If
            // any of those inputs changed, invalidate the cached trio so the
            // "Choose your plan" step re-fetches for the new criteria.
            const code_changed = destination.code !== undefined && destination.code !== prev.code
            const arrival_changed = destination.arrival_date !== undefined && destination.arrival_date !== prev.arrival_date
            const departure_changed = destination.departure_date !== undefined && destination.departure_date !== prev.departure_date
            if (prev.trip_id && (code_changed || arrival_changed || departure_changed)) {
                commit('CLEAR_TRAVEL_ESIM_RECOMMENDATION', prev.trip_id)
            }
            persist(state)
        },

        // Select a plan for a specific trip (keyed by trip_id so two trips to the
        // same country stay independent).
        travelEsimSelectPlan({ commit, state }, { trip_id, plan }) {
            const selected_plans = { ...state.travel_esim_selected_plans, [trip_id]: plan }
            commit('SET_TRAVEL_ESIM_SELECTED_PLANS', selected_plans)
            persist(state)
        },

        // --- SIMs: the SIM list is the canonical model on the SIM step ---

        travelEsimSetSimCount({ commit, state }, count) {
            const sims = [...state.travel_esim_sims]
            while (sims.length < count) {
                sims.push({ name: '', plans: [] })
            }
            while (sims.length > count) {
                sims.pop()
            }
            commit('SET_TRAVEL_ESIM_SIM_COUNT', sims.length)
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            persist(state)
        },

        // Add a new empty SIM (no plans yet)
        travelEsimAddSim({ commit, state }) {
            const sims = [...state.travel_esim_sims, { name: '', plans: [] }]
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            commit('SET_TRAVEL_ESIM_SIM_COUNT', sims.length)
            persist(state)
        },

        travelEsimRemoveSim({ commit, state }, index) {
            const sims = state.travel_esim_sims.filter((_, i) => i !== index)
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            commit('SET_TRAVEL_ESIM_SIM_COUNT', sims.length)
            persist(state)
        },

        travelEsimUpdateSim({ commit, state }, { index, sim }) {
            const sims = [...state.travel_esim_sims]
            sims[index] = { ...sims[index], ...sim }
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            persist(state)
        },

        travelEsimRemoveSimPlan({ commit, state }, { sim_index, plan_index }) {
            const sims = [...state.travel_esim_sims]
            sims[sim_index] = { ...sims[sim_index], plans: sims[sim_index].plans.filter((_, i) => i !== plan_index) }
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            persist(state)
        },

        // Ensure at least one SIM exists; seed the FIRST sim with the trip plans
        // the customer chose (only when it has no plans yet). Idempotent.
        travelEsimEnsureFirstSim({ commit, state }) {
            const sims = [...state.travel_esim_sims]
            if (sims.length === 0) {
                sims.push({ name: '', plans: buildTripPlans(state) })
            } else if ((!sims[0].plans || sims[0].plans.length === 0)) {
                sims[0] = { ...sims[0], plans: buildTripPlans(state) }
            }
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            commit('SET_TRAVEL_ESIM_SIM_COUNT', sims.length)
            persist(state)
        },

        // Customer tapped "Choose plan"/pencil on a SIM -> remember which SIM
        // (and which exact plan slot, when editing an existing plan), and sync
        // selected_plans to reflect THAT sim's current plans so the plan step
        // shows the right selections (no stale leftovers from other sims).
        // plan_index = -1 means "add a new plan" rather than edit an existing one.
        travelEsimChoosePlanForSim({ commit, state }, { sim_index, plan_index = -1 } = {}) {
            commit('SET_TRAVEL_ESIM_ACTIVE_SIM_INDEX', sim_index)
            commit('SET_TRAVEL_ESIM_ACTIVE_PLAN_INDEX', plan_index)

            const sim = state.travel_esim_sims[sim_index]
            const selected_plans = {}
            if (sim && sim.plans) {
                sim.plans.forEach(p => {
                    if (p.trip_id) selected_plans[p.trip_id] = p
                })
            }
            commit('SET_TRAVEL_ESIM_SELECTED_PLANS', selected_plans)
            persist(state)
        },

        // Add or update a single plan (chosen on the "All travel plans" page) on
        // the active SIM. If a specific plan slot is being edited (pencil), that
        // EXACT slot is replaced. Otherwise (adding), a plan for the same region
        // is replaced if present, else the plan is appended. Creates the first
        // SIM if none exist.
        travelEsimAddPlanToActiveSim({ commit, state }, plan) {
            const sims = state.travel_esim_sims.length
                ? [...state.travel_esim_sims]
                : [{ name: '', plans: [] }]

            const index = sims[state.travel_esim_active_sim_index]
                ? state.travel_esim_active_sim_index
                : 0

            const existing = sims[index].plans || []
            const edit_index = state.travel_esim_active_plan_index

            let plans
            if (edit_index > -1 && existing[edit_index]) {
                // Editing a specific plan slot -> replace exactly that one
                plans = [...existing]
                plans[edit_index] = plan
            } else {
                const region_index = existing.findIndex(p => p.region === plan.region)
                if (region_index > -1) {
                    plans = [...existing]
                    plans[region_index] = plan
                } else {
                    plans = [...existing, plan]
                }
            }

            sims[index] = { ...sims[index], plans }

            commit('SET_TRAVEL_ESIM_SIMS', sims)
            commit('SET_TRAVEL_ESIM_SIM_COUNT', sims.length)
            // Editing is complete — clear the edit target
            commit('SET_TRAVEL_ESIM_ACTIVE_PLAN_INDEX', -1)
            persist(state)
        },

        // Commit the plans chosen on the plan step back into the active SIM.
        // MERGE (not replace): the trip-derived plans upsert by region, while any
        // existing plans on the SIM for regions NOT covered by the current trip
        // selection are preserved (e.g. plans added via the "All travel plans"
        // browse flow). This prevents cross-flow plans from being wiped.
        travelEsimCommitPlansToActiveSim({ commit, state }) {
            const index = state.travel_esim_active_sim_index
            const sims = [...state.travel_esim_sims]

            if (sims.length === 0) sims.push({ name: '', plans: [] })
            const target = sims[index] ? index : 0

            const tripPlans = buildTripPlans(state)
            const tripRegions = new Set(tripPlans.map(p => p.region))
            const preserved = (sims[target].plans || []).filter(p => !tripRegions.has(p.region))

            sims[target] = { ...sims[target], plans: [...preserved, ...tripPlans] }
            commit('SET_TRAVEL_ESIM_SIMS', sims)
            commit('SET_TRAVEL_ESIM_SIM_COUNT', sims.length)
            commit('SET_TRAVEL_ESIM_ACTIVE_PLAN_INDEX', -1)
            persist(state)
        },

        travelEsimSetActiveSimIndex({ commit }, index) {
            commit('SET_TRAVEL_ESIM_ACTIVE_SIM_INDEX', index)
        },

        travelEsimReset({ commit, state }) {
            commit('SET_TRAVEL_ESIM_STEP', 0)
            commit('SET_TRAVEL_ESIM_DESTINATIONS', [])
            commit('SET_TRAVEL_ESIM_SELECTED_PLANS', {})
            commit('SET_TRAVEL_ESIM_SIMS', [])
            commit('SET_TRAVEL_ESIM_SIM_COUNT', 1)
            commit('SET_TRAVEL_ESIM_ACTIVE_SIM_INDEX', 0)
            commit('SET_TRAVEL_ESIM_ACTIVE_PLAN_INDEX', -1)
            commit('SET_TRAVEL_ESIM_TRIP_SEQ', 0)
            commit('RESET_TRAVEL_ESIM_RECOMMENDATIONS')
            dynStorage.set(STORAGE_KEY, null)
        },

        travelEsimLoadFromStorage({ commit }) {
            const stored = dynStorage.get(STORAGE_KEY)
            if (stored) {
                if (stored.destinations) commit('SET_TRAVEL_ESIM_DESTINATIONS', stored.destinations)
                if (stored.selected_plans) commit('SET_TRAVEL_ESIM_SELECTED_PLANS', stored.selected_plans)
                if (stored.sims) commit('SET_TRAVEL_ESIM_SIMS', stored.sims)
                if (stored.active_sim_index !== undefined) commit('SET_TRAVEL_ESIM_ACTIVE_SIM_INDEX', stored.active_sim_index)
                if (stored.trip_seq !== undefined) commit('SET_TRAVEL_ESIM_TRIP_SEQ', stored.trip_seq)
                if (stored.step !== undefined) commit('SET_TRAVEL_ESIM_STEP', stored.step)
            }
        },
    },

    mutations: {
        SET_TRAVEL_ESIM_STEP(state, step) {
            state.travel_esim_current_step = step
        },

        SET_TRAVEL_ESIM_DESTINATIONS(state, destinations) {
            state.travel_esim_destinations = destinations
        },

        SET_TRAVEL_ESIM_SELECTED_PLANS(state, selected_plans) {
            state.travel_esim_selected_plans = selected_plans
        },

        SET_TRAVEL_ESIM_SIMS(state, sims) {
            state.travel_esim_sims = sims
        },

        SET_TRAVEL_ESIM_SIM_COUNT(state, count) {
            state.travel_esim_sim_count = count
        },

        SET_TRAVEL_ESIM_ACTIVE_SIM_INDEX(state, index) {
            state.travel_esim_active_sim_index = index
        },

        SET_TRAVEL_ESIM_ACTIVE_PLAN_INDEX(state, index) {
            state.travel_esim_active_plan_index = index
        },

        SET_TRAVEL_ESIM_TRIP_SEQ(state, seq) {
            state.travel_esim_trip_seq = seq
        },

        SAVE_TRAVEL_ESIM_CATALOG(state, catalog) {
            state.travel_esim_catalog = catalog
            state.travel_esim_catalog_loaded = true
            travelCache.fix()
        },

        RESET_TRAVEL_ESIM_CATALOG(state) {
            state.travel_esim_catalog = []
            state.travel_esim_catalog_loaded = false
            travelCache.reset()
        },

        SET_TRAVEL_ESIM_CATALOG_LOADED(state, loaded) {
            state.travel_esim_catalog_loaded = loaded
        },

        SET_TRAVEL_ESIM_RECOMMENDATIONS(state, { trip_id, plans }) {
            state.travel_esim_recommendations = {
                ...state.travel_esim_recommendations,
                [trip_id]: plans,
            }
        },

        SET_TRAVEL_ESIM_RECOMMEND_LOADING(state, { trip_id, loading }) {
            state.travel_esim_recommend_loading = {
                ...state.travel_esim_recommend_loading,
                [trip_id]: loading,
            }
        },

        // Drop the cached recommendation for a trip (its inputs changed, or it
        // was removed) so the next visit re-fetches.
        CLEAR_TRAVEL_ESIM_RECOMMENDATION(state, trip_id) {
            const recommendations = { ...state.travel_esim_recommendations }
            const loading = { ...state.travel_esim_recommend_loading }
            delete recommendations[trip_id]
            delete loading[trip_id]
            state.travel_esim_recommendations = recommendations
            state.travel_esim_recommend_loading = loading
        },

        RESET_TRAVEL_ESIM_RECOMMENDATIONS(state) {
            state.travel_esim_recommendations = {}
            state.travel_esim_recommend_loading = {}
        },
    },
}
